import type { APIDefinitionsReadType } from '@readme/api/src/mappings/apis/types';

import React, { useMemo, useState } from 'react';

import useBuildRoute from '@core/hooks/useBuildRoute';
import useClassy from '@core/hooks/useClassy';
import { useProjectStore, useSuperHubStore } from '@core/store';

import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Menu, { MenuItem, MenuDivider } from '@ui/Menu';
import Spinner from '@ui/Spinner';
import Timestamp from '@ui/Timestamp';

import styles from './index.module.scss';
import ApiDefinitionStatusBadge from './StatusBadge';
import ApiDefinitionType from './Type';

const sourceMap: Record<APIDefinitionsReadType['source']['current'], string> = {
  api: 'API',
  /** @deprecated Use `apidesigner` instead. This will be removed when it's removed from Gitto. */
  apieditor: 'the API Designer',
  apidesigner: 'the API Designer',
  bidi: 'Git',
  form: 'File Upload',
  postman: 'Postman',
  rdme: 'CLI',
  rdme_github: 'GitHub Actions',
  url: 'URL',
};

interface ApiDefinitionItemProps {
  definition: APIDefinitionsReadType;
  deleteConfirmation: () => Promise<void> | void; // ai wrote this, plz help
  onEdit?: (definition: APIDefinitionsReadType) => void;
  onReplace?: (definition: APIDefinitionsReadType) => void;
}

const ApiDefinitionItem: React.FC<React.PropsWithChildren<ApiDefinitionItemProps>> = ({
  definition,
  deleteConfirmation,
  onReplace,
  onEdit,
}) => {
  const bem = useClassy(styles, 'ApiDefinitionListItem');
  const allowEditing = useProjectStore(s => s.data.api_designer.allow_editing);
  const currentBranch = useSuperHubStore(s => s.currentBranch);
  const buildRoute = useBuildRoute();

  const isUpdating = ['pending', 'pending_update'].includes(definition.upload.status);
  const isErrored = ['failed', 'failed_update'].includes(definition.upload.status);

  const [expandError, setExpandError] = useState(false);
  const errorsToShow =
    isErrored && !!definition.upload.reason
      ? definition.upload.reason
      : definition.upload.status === 'done' && !!definition.upload.warnings
        ? definition.upload.warnings
        : false;

  /** Contains the URL to the raw definition for the current branch. */
  const viewDefinitionUrl = useMemo(() => {
    return buildRoute({
      includeBaseUrl: true,
      pathnameSegments: [`v${encodeURIComponent(currentBranch.name)}`, 'openapi', definition.filename],
    });
  }, [buildRoute, currentBranch.name, definition.filename]);

  return (
    <Flex align="stretch" className={bem('&')} gap="0" layout="col" tag="li">
      <Flex align="center" className={bem('-header')} gap="sm" justify="between">
        <Flex align="center" className={bem('&-label')} gap="sm" wrap="wrap">
          {definition.filename}
          <ApiDefinitionType type={definition.type} />
        </Flex>
        <Flex align="center" className={bem('&-right')} gap="xs">
          <Dropdown className={bem('-list-dropdown')}>
            <Button aria-label="More options" kind="secondary" size="sm" text>
              Update
              <Icon aria-hidden="true" name="chevron-down" />
            </Button>
            <Menu>
              <MenuItem href={viewDefinitionUrl} TagName="a" target="_blank">
                <Icon aria-hidden="true" name="oas" />
                <span>View OAS File</span>
              </MenuItem>
              <MenuDivider />
              <MenuItem disabled={allowEditing === 'disabled'} onClick={() => onEdit?.(definition)}>
                <Icon aria-hidden="true" name="edit" />
                <span>Edit Definition</span>
              </MenuItem>
              <MenuItem onClick={() => onReplace?.(definition)}>
                <Icon aria-hidden="true" name="repeat" />
                <span>Update OAS File</span>
              </MenuItem>
              <MenuDivider />
              <MenuItem color="red" onClick={deleteConfirmation}>
                <Icon aria-hidden="true" name="trash" />
                <span>Delete</span>
              </MenuItem>
            </Menu>
          </Dropdown>
        </Flex>
      </Flex>

      <div className={bem('-status', isErrored && '-status_error')}>
        <div className={bem('-status-info', isUpdating && '-status-info_loading')}>
          {isUpdating ? (
            <>
              <Spinner size="sm" />
              Updating…
            </>
          ) : (
            <>
              <span className={bem('-status-info-title')}>Updated</span>
              <Timestamp value={definition.updated_at} />
              <span>•</span>
              {sourceMap[definition.source.current]}
              <ApiDefinitionStatusBadge status={definition.upload.status} />
            </>
          )}
        </div>
        {!!errorsToShow && (
          <pre className={bem('-status-error')}>
            <code className={bem('-status-error-code', expandError && '-status-error-code_expanded')}>
              {errorsToShow}
            </code>
            {errorsToShow.split(/\r\n|\r|\n/).length >= 13 && !expandError && (
              <Button
                className={bem('-status-error-button')}
                kind="secondary"
                onClick={() => setExpandError(!expandError)}
                outline
                size="sm"
              >
                Expand
              </Button>
            )}
          </pre>
        )}
      </div>
    </Flex>
  );
};

export default ApiDefinitionItem;
